import { readonly, ref, type Ref } from "vue";
import { tryOnScopeDispose } from "@vueuse/core";

/** What a share button shows after a clipboard fallback: nothing, "copied" or "copy failed". */
export type ShareFeedback = 'idle' | 'copied' | 'error';

/** How one share() call ended. 'dismissed' is the reader closing the native share sheet. */
export type ShareOutcome = 'shared' | 'dismissed' | 'copied' | 'failed';

export interface ShareRequest {
    url: string;
    title?: string;
    text?: string;
}

/** How long the copied / failed feedback stays before the button goes back to "Share". */
export const SHARE_FEEDBACK_MS = 2000;

/**
 * An absolute link to an in-app path, for sharing. VITE_ORIGIN first, so a link copied from a preview or a local build
 * names the configured site; the page's own origin otherwise.
 */
export function appUrl( path: string ): string {
    const configured = `${ import.meta.env.VITE_ORIGIN ?? '' }`.trim();
    const fallback = typeof window !== 'undefined' ? window.location.origin : '';
    const origin = ( configured || fallback ).replace( /\/+$/, '' );
    return `${ origin }${ path.startsWith( '/' ) ? path : `/${ path }` }`;
}

/**
 * Share a link: the native share sheet where there is one (phones, the installed PWA, which has no address bar to copy
 * from), otherwise copy it to the clipboard and show `feedback` for SHARE_FEEDBACK_MS. Closing the share sheet is a
 * choice, not a failure, so it neither falls back to the clipboard nor shows anything. Any other share-sheet failure
 * (no user activation, a blocked permission) falls back to the clipboard.
 *
 * Call in setup: the feedback timer is cleared with the component, and a share still in flight at that point no longer
 * touches the feedback.
 */
export function useShare(): { feedback: Readonly< Ref< ShareFeedback > >; share: ( request: ShareRequest ) => Promise< ShareOutcome > } {
    const feedback = ref< ShareFeedback >( 'idle' );
    let resetTimer: ReturnType< typeof setTimeout > | null = null;
    let disposed = false;

    function clearResetTimer(): void {
        if ( resetTimer !== null ) {
            clearTimeout( resetTimer );
            resetTimer = null;
        }
    }

    function show( next: ShareFeedback ): void {
        if ( disposed ) {
            return;
        }
        feedback.value = next;
        clearResetTimer();
        resetTimer = setTimeout( () => {
            feedback.value = 'idle';
            resetTimer = null;
        }, SHARE_FEEDBACK_MS );
    }

    async function share( { url, title, text }: ShareRequest ): Promise< ShareOutcome > {
        if ( typeof navigator !== 'undefined' && typeof navigator.share === 'function' ) {
            try {
                await navigator.share( { url, ...( title ? { title } : {} ), ...( text ? { text } : {} ) } );
                return 'shared';
            } catch ( err: unknown ) {
                if ( ( err as { name?: string } | null )?.name === 'AbortError' ) {
                    return 'dismissed';
                }
            }
        }

        try {
            await navigator.clipboard.writeText( url );
            show( 'copied' );
            return 'copied';
        } catch {
            show( 'error' );
            return 'failed';
        }
    }

    tryOnScopeDispose( () => {
        disposed = true;
        clearResetTimer();
    } );

    return { feedback: readonly( feedback ), share };
}
